import { DEFAULT_UNTITLED_NODE_NAME } from "src/constants/DefaultValues";
import type { BeMappedNode } from "src/constants/NodeDataTypes";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { getNodeTitleFromSelectedNode } from "src/nodeEditor/SidePaneHeader";
import { useGraphStore } from "src/store/StoreProvider";
import { useCopilotContextualEntities } from "src/unifiedCopilot/context/hooks";
import type { ReferenceContextualEntity } from "src/unifiedCopilot/context/types";

/**
 * The input and output nodes cannot be renamed, and the canvas labels them by
 * type alone, so their stored name is never what the user sees.
 */
const isTypeTitledNode = (node: BeMappedNode): boolean =>
  node.type === NODE_TYPE.INPUT_NODE || node.type === NODE_TYPE.OUTPUT_NODE;

/**
 * What a node is called in the copilot. An untitled node keeps its placeholder
 * so it still reads as unnamed; the type's UI title in brackets tells two
 * untitled nodes apart without exposing a technical type string.
 */
export const getCopilotNodeDisplayName = (node: BeMappedNode): string => {
  if (isTypeTitledNode(node)) {
    return getNodeTitleFromSelectedNode(node) ?? node.data.label;
  }
  if (node.data.label !== DEFAULT_UNTITLED_NODE_NAME) {
    return node.data.label;
  }
  const nodeTypeTitle = getNodeTitleFromSelectedNode(node);
  return nodeTypeTitle
    ? `${DEFAULT_UNTITLED_NODE_NAME} (${nodeTypeTitle})`
    : DEFAULT_UNTITLED_NODE_NAME;
};

export const mapNodesToCopilotContextualEntities = (
  nodes: BeMappedNode[],
  versionId: string,
): ReferenceContextualEntity[] =>
  nodes.map((node) => ({
    kind: "reference",
    type: "node",
    id: node.id,
    displayName: getCopilotNodeDisplayName(node),
    ...(node.type ? { subtype: node.type } : {}),
    parent: {
      type: "flow-version",
      id: versionId,
    },
  }));

export const useFlowVersionNodeCopilotContext = (versionId: string): void => {
  const { nodesArray, versionId: graphVersionId } = useGraphStore();
  const entities =
    graphVersionId === versionId
      ? mapNodesToCopilotContextualEntities(nodesArray, versionId)
      : [];

  useCopilotContextualEntities(entities);
};

/**
 * The node whose side pane is open goes to the agent on every turn and shows
 * as a pill. Mount-scoped to the pane, so closing it releases the node back to
 * mention-only.
 */
export const useOpenedNodeCopilotContext = (versionId: string): void => {
  const { selectedNode, versionId: graphVersionId } = useGraphStore();
  const entities =
    selectedNode && graphVersionId === versionId
      ? mapNodesToCopilotContextualEntities([selectedNode], versionId)
      : [];

  useCopilotContextualEntities(entities, { includeInAgentContext: true });
};
